import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getShopById, getAvailableSlots, createBooking } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import Toast from "../components/Toast";

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api" });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function BookingPage() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [shop, setShop] = useState(null);
  const [services, setServices] = useState([]);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ message, type });
  const [waOpened, setWaOpened] = useState(false);

  const totalPrice = selectedServices.reduce((sum, s) => sum + parseFloat(s.price), 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + parseInt(s.duration), 0);

  const toggleService = (service) => {
    setSelectedServices((prev) => {
      const exists = prev.find((s) => s.id === service.id);
      if (exists) return prev.filter((s) => s.id !== service.id);
      return [...prev, service];
    });
    setCouponResult(null);
    setCouponCode("");
  };

  const isSelected = (serviceId) => selectedServices.some((s) => s.id === serviceId);

  useEffect(() => {
    const fetchShop = async () => {
      try {
        const res = await getShopById(shopId);
        setShop(res.data.shop || res.data);
        setServices(res.data.services || []);
        try {
          const couponRes = await API.get(`/coupons/shop/${shopId}/public`);
          setAvailableCoupons(couponRes.data || []);
        } catch (e) { setAvailableCoupons([]); }
      } catch (err) { setError("Could not load shop details."); }
      finally { setPageLoading(false); }
    };
    fetchShop();
  }, [shopId]);

  useEffect(() => {
    if (selectedDate && selectedServices.length > 0) fetchSlots();
  }, [selectedDate, selectedServices]);

  const fetchSlots = async () => {
    setSlotsLoading(true); setSelectedSlot(null); setError("");
    try {
      const res = await getAvailableSlots(shopId, selectedDate, selectedServices[0].id);
      setSlots(res.data.slots || res.data || []);
    } catch (err) { setError("Could not load available slots."); }
    finally { setSlotsLoading(false); }
  };

  const handleApplyCoupon = async (code) => {
    const applyCode = code || couponCode;
    if (!applyCode.trim()) return;
    setCouponLoading(true); setCouponError(""); setCouponResult(null);
    setCouponCode(applyCode);
    try {
      const res = await API.post("/coupons/validate", {
        code: applyCode, shop_id: shopId, amount: totalPrice,
      });
      setCouponResult(res.data);
    } catch (err) { setCouponError(err.response?.data?.message || "Invalid coupon code."); }
    finally { setCouponLoading(false); }
  };

  const handleConfirmBooking = async () => {
    setLoading(true); setError("");
    try {
      const serviceNames = selectedServices.map((s) => s.name).join(", ");
      const finalAmount = couponResult?.final_amount || totalPrice;
      const res = await createBooking({
        shop_id: shopId,
        service_id: selectedServices[0].id,
        booking_date: selectedDate,
        booking_time: selectedSlot,
        notes: `Services: ${serviceNames}${notes ? `\n${notes}` : ""}`,
        coupon_id: couponResult?.coupon_id || null,
        final_amount: finalAmount,
      });
      setBooking(res.data.booking);
      setStep(4);
    } catch (err) { setError(err.response?.data?.message || "Could not confirm booking."); }
    finally { setLoading(false); }
  };

  // WhatsApp handler — only triggered manually by customer
  const handleWhatsAppNotify = () => {
    const phone = shop?.phone?.replace(/[^0-9]/g, "");
    if (!phone) {
      showToast("This shop has no phone number listed.", "warning");
      return;
    }
    if (waOpened) {
      showToast("✅ You have already notified the salon!", "success");
      return;
    }
    const finalAmount = couponResult?.final_amount || totalPrice;
    const serviceNames = selectedServices.map((s) => s.name).join(", ");
    const message =
      `Hello! I just booked an appointment at *${shop?.name}*.\n\n` +
      `👤 Customer: *${user?.name}*\n` +
      `📞 My Phone: *${user?.phone}*\n` +
      `💈 Services: *${serviceNames}*\n` +
      `⏱ Total Duration: *${totalDuration} mins*\n` +
      `📅 Date: *${formatDate(selectedDate)}*\n` +
      `🕐 Time: *${formatTime(selectedSlot)}*\n` +
      `💰 Amount: *₹${finalAmount}*\n\n` +
      `Please confirm my appointment. Thank you! 🙏`;

    setWaOpened(true);
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const getTodayDate = () => new Date().toISOString().split("T")[0];

  if (pageLoading) return <div style={styles.center}><p style={{ color: "var(--text-muted)" }}>Loading...</p></div>;

  // SUCCESS PAGE
  if (step === 4 && booking) {
    const finalAmount = couponResult?.final_amount || totalPrice;
    const serviceNames = selectedServices.map((s) => s.name).join(", ");
    return (
      <div style={styles.center}>
        <div style={styles.successCard}>
          <p style={{ fontSize: "60px", marginBottom: "12px" }}>🎉</p>
          <h2 style={styles.successTitle}>Booking Confirmed!</h2>
          <p style={styles.successSubtitle}>Your appointment is booked successfully</p>
          <div style={styles.successDetails}>
            <SRow icon="✂️" label="Shop" value={shop?.name} />
            <SRow icon="💈" label="Services" value={serviceNames} />
            <SRow icon="⏱" label="Duration" value={`${totalDuration} mins`} />
            <SRow icon="📅" label="Date" value={formatDate(selectedDate)} />
            <SRow icon="🕐" label="Time" value={formatTime(selectedSlot)} />
            <SRow icon="💰" label="Pay at Salon" value={`₹${finalAmount}`} />
            {couponResult && <SRow icon="🎟️" label="You Saved" value={`₹${couponResult.discount_amount}`} valueColor="#4caf50" />}
          </div>
          <div style={styles.payNote}>💡 Pay <strong>₹{finalAmount}</strong> directly to Salon</div>

          {shop?.phone && (
            <div style={styles.whatsappSection}>
              <p style={styles.whatsappHint}>
                {waOpened
                  ? "✅ Salon has been notified about your booking!"
                  : "⚠️ Notify the salon — bookings without notification may experience delays or cancellation!"}
              </p>
              <button
                onClick={handleWhatsAppNotify}
                style={{ ...styles.whatsappBtn, background: waOpened ? "#128c3e" : "#25d366" }}
              >
                <span style={{ fontSize: "20px" }}>💬</span>
                {waOpened ? "✅ Salon Notified!" : "Notify Salon on WhatsApp"}
              </button>
              <p style={styles.whatsappNote}>
                {waOpened
                  ? "Tap again only if the salon did not receive your message"
                  : "⏰ There may be a delay in service if the salon is not notified"}
              </p>
            </div>
          )}

          <div style={styles.successBtns}>
            <button onClick={() => navigate("/my-bookings")} style={styles.myBookingsBtn}>📅 My Bookings</button>
            <button onClick={() => navigate("/shops")} style={styles.homeBtn}>🏠 Shops</button>
          </div>
        </div>
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "" })} />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button onClick={() => step === 1 ? navigate(`/shops/${shopId}`) : setStep(step - 1)} style={styles.backBtn}>← Back</button>
        <h2 style={styles.headerTitle}>Book Appointment</h2>
        <div />
      </div>

      <div style={styles.stepRow}>
        {["Services", "Date & Time", "Confirm"].map((label, i) => (
          <div key={i} style={styles.stepItem}>
            <div style={{ ...styles.stepCircle, ...(step === i + 1 ? styles.stepActive : {}), ...(step > i + 1 ? styles.stepDone : {}) }}>
              {step > i + 1 ? "✓" : i + 1}
            </div>
            <span style={styles.stepLabel}>{label}</span>
          </div>
        ))}
      </div>

      <div style={styles.container}>
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* STEP 1 */}
        {step === 1 && (
          <div>
            <div style={styles.shopInfo}>
              <p style={styles.shopInfoName}>{shop?.name}</p>
              <p style={styles.shopInfoCity}>📍 {shop?.address}, {shop?.city}</p>
            </div>
            <div style={styles.serviceHeaderRow}>
              <h3 style={styles.sectionTitle}>Select Services</h3>
              <span style={styles.multiHint}>Tap to select multiple</span>
            </div>
            <div style={styles.multiTip}>
              💡 You can select more than one service. Total price will be calculated automatically.
            </div>
            {services.map((service) => (
              <div key={service.id} onClick={() => toggleService(service)}
                style={{ ...styles.serviceCard, ...(isSelected(service.id) ? styles.serviceCardSelected : {}) }}>
                <div style={styles.serviceInfo}>
                  <p style={styles.serviceName}>{service.name}</p>
                  {service.description && <p style={styles.serviceDesc}>{service.description}</p>}
                  <p style={styles.serviceDuration}>⏱ {service.duration} mins</p>
                </div>
                <div style={styles.servicePrice}>
                  <p style={styles.priceText}>₹{service.price}</p>
                  <div style={{ ...styles.checkbox, ...(isSelected(service.id) ? styles.checkboxSelected : {}) }}>
                    {isSelected(service.id) && <span style={{ color: "white", fontSize: "12px" }}>✓</span>}
                  </div>
                </div>
              </div>
            ))}
            {selectedServices.length > 0 && (
              <div style={styles.selectionSummary}>
                <div>
                  <p style={styles.summaryText}>{selectedServices.length} service{selectedServices.length > 1 ? "s" : ""} selected</p>
                  <p style={styles.summaryNames}>{selectedServices.map((s) => s.name).join(" + ")}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={styles.summaryTotal}>₹{totalPrice}</p>
                  <p style={styles.summaryDuration}>⏱ {totalDuration} mins</p>
                </div>
              </div>
            )}
            <button onClick={() => { if (selectedServices.length > 0) setStep(2); }}
              style={{ ...styles.nextBtn, opacity: selectedServices.length > 0 ? 1 : 0.4, cursor: selectedServices.length > 0 ? "pointer" : "not-allowed" }}>
              Next → Choose Date & Time
            </button>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div>
            <div style={styles.selectedBanner}>
              {selectedServices.map((s) => s.name).join(" + ")} · ₹{totalPrice} · {totalDuration} mins total
            </div>
            <h3 style={styles.sectionTitle}>Select Date</h3>
            <input type="date" value={selectedDate} min={getTodayDate()}
              onChange={(e) => setSelectedDate(e.target.value)} style={styles.dateInput} />
            {selectedDate && (
              <>
                <h3 style={styles.sectionTitle}>Select Time Slot</h3>
                {slotsLoading ? (
                  <p style={{ color: "var(--text-muted)", textAlign: "center", padding: "20px" }}>Loading slots...</p>
                ) : slots.length === 0 ? (
                  <div style={styles.noSlots}>
                    <p style={{ fontSize: "32px" }}>😕</p>
                    <p style={{ color: "var(--text-muted)" }}>No slots for this date. Try another!</p>
                  </div>
                ) : (
                  <div style={styles.slotsGrid}>
                    {slots.map((slot) => (
                      <div key={slot.time} onClick={() => slot.available && setSelectedSlot(slot.time)}
                        style={{ ...styles.slotBtn, ...(!slot.available ? styles.slotBooked : {}), ...(selectedSlot === slot.time ? styles.slotSelected : {}) }}>
                        {formatTime(slot.time)}
                        {!slot.available && <span style={{ display: "block", fontSize: "9px", color: "var(--text-muted)" }}>Booked</span>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <button onClick={() => { if (selectedDate && selectedSlot) setStep(3); }}
              style={{ ...styles.nextBtn, opacity: selectedDate && selectedSlot ? 1 : 0.4, cursor: selectedDate && selectedSlot ? "pointer" : "not-allowed" }}>
              Next → Confirm Booking
            </button>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div>
            <h3 style={styles.sectionTitle}>Booking Summary</h3>
            <div style={styles.summaryCard}>
              <SRow icon="✂️" label="Shop" value={shop?.name} />
              {selectedServices.map((s, i) => (
                <SRow key={s.id} icon="💈" label={i === 0 ? "Services" : ""} value={`${s.name} — ₹${s.price}`} />
              ))}
              <SRow icon="⏱" label="Total Duration" value={`${totalDuration} mins`} />
              <SRow icon="📅" label="Date" value={formatDate(selectedDate)} />
              <SRow icon="🕐" label="Time" value={formatTime(selectedSlot)} />
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: "1px solid var(--border)", marginTop: "4px" }}>
                <span style={{ color: "white", fontWeight: "bold", fontSize: "14px" }}>💰 Total</span>
                <span style={{ color: "var(--accent)", fontWeight: "900", fontSize: "1.1rem" }}>₹{totalPrice}</span>
              </div>
            </div>

            {availableCoupons.length > 0 && !couponResult && (
              <div style={styles.availableCouponsBox}>
                <p style={styles.availableCouponsTitle}>🎟️ Available Offers — Tap to Apply!</p>
                {availableCoupons.map((coupon) => (
                  <div key={coupon.id} style={styles.availableCouponChip}
                    onClick={() => handleApplyCoupon(coupon.code)}>
                    <div>
                      <span style={styles.chipCode}>{coupon.code}</span>
                      <span style={styles.chipDiscount}>
                        {coupon.discount_type === "percentage"
                          ? ` — ${coupon.discount_value}% OFF`
                          : ` — ₹${coupon.discount_value} OFF`}
                      </span>
                    </div>
                    <span style={styles.chipApply}>Tap to Apply →</span>
                  </div>
                ))}
              </div>
            )}

            <div style={styles.couponBox}>
              <p style={styles.couponTitle}>🎟️ Have a Coupon Code?</p>
              {selectedServices.length > 1 && (
                <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "0 0 10px" }}>
                  Discount applies on total bill of ₹{totalPrice}
                </p>
              )}
              {!couponResult ? (
                <>
                  <div style={styles.couponRow}>
                    <input placeholder="ENTER CODE" value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(""); }}
                      style={styles.couponInput} />
                    <button onClick={() => handleApplyCoupon()} style={styles.applyBtn} disabled={couponLoading || !couponCode}>
                      {couponLoading ? "..." : "Apply"}
                    </button>
                  </div>
                  {couponError && <p style={{ color: "var(--accent)", fontSize: "13px", marginTop: "8px" }}>❌ {couponError}</p>}
                </>
              ) : (
                <div style={styles.couponSuccess}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <p style={{ color: "#4caf50", fontWeight: "bold", fontSize: "14px", margin: 0 }}>{couponResult.message}</p>
                    <button onClick={() => { setCouponCode(""); setCouponResult(null); }} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}>✕</button>
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    <div style={styles.priceRow}><span style={{ color: "var(--text-muted)" }}>Total</span><span style={{ color: "white" }}>₹{couponResult.original_amount}</span></div>
                    <div style={styles.priceRow}><span style={{ color: "#4caf50" }}>Discount</span><span style={{ color: "#4caf50" }}>- ₹{couponResult.discount_amount}</span></div>
                    <div style={{ ...styles.priceRow, fontWeight: "bold", borderTop: "1px solid var(--border)", paddingTop: "8px" }}>
                      <span style={{ color: "white" }}>Final</span>
                      <span style={{ color: "var(--accent)", fontSize: "1.1rem" }}>₹{couponResult.final_amount}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <label style={styles.notesLabel}>📝 Special Instructions (optional)</label>
            <textarea placeholder="e.g. I want a fade cut..." value={notes}
              onChange={(e) => setNotes(e.target.value)} style={styles.textarea} rows={3} />

            <div style={styles.payAtShop}>
              💡 Pay <strong>₹{couponResult ? couponResult.final_amount : totalPrice}</strong> directly to Salon. No online payment!
            </div>

            <button onClick={handleConfirmBooking} style={styles.confirmBtn} disabled={loading}>
              {loading ? "Confirming..." : `✅ Confirm Booking — ₹${couponResult ? couponResult.final_amount : totalPrice}`}
            </button>
          </div>
        )}
      </div>

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "" })} />
    </div>
  );
}

function SRow({ icon, label, value, valueColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>{icon} {label}</span>
      <span style={{ fontWeight: "600", fontSize: "13px", color: valueColor || "white", textAlign: "right", maxWidth: "60%" }}>{value}</span>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  let hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${m} ${ampm}`;
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)", paddingBottom: "40px" },
  center: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: "20px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)" },
  headerTitle: { fontSize: "17px", fontWeight: "bold", color: "white" },
  backBtn: { background: "none", border: "1px solid var(--border)", color: "var(--text-secondary)", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontSize: "14px" },
  stepRow: { display: "flex", justifyContent: "center", gap: "16px", padding: "16px 20px", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)" },
  stepItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" },
  stepCircle: { width: "32px", height: "32px", borderRadius: "50%", background: "var(--bg-card)", color: "var(--text-muted)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "13px" },
  stepActive: { background: "var(--accent)", color: "white", border: "1px solid var(--accent)" },
  stepDone: { background: "#4caf50", color: "white", border: "1px solid #4caf50" },
  stepLabel: { fontSize: "11px", color: "var(--text-muted)" },
  container: { maxWidth: "560px", margin: "0 auto", padding: "20px 16px" },
  errorBox: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid rgba(233,69,96,0.3)" },
  shopInfo: { background: "var(--bg-card)", borderRadius: "12px", padding: "16px", marginBottom: "20px", border: "1px solid var(--border)" },
  shopInfoName: { fontWeight: "bold", fontSize: "16px", color: "white", margin: "0 0 4px" },
  shopInfoCity: { color: "var(--text-muted)", fontSize: "13px", margin: 0 },
  serviceHeaderRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" },
  sectionTitle: { fontSize: "15px", fontWeight: "bold", color: "white", marginBottom: "0" },
  multiHint: { color: "var(--accent)", fontSize: "12px", fontWeight: "600" },
  multiTip: { background: "rgba(233,69,96,0.08)", border: "1px solid rgba(233,69,96,0.2)", borderRadius: "8px", padding: "10px 12px", fontSize: "12px", color: "var(--text-muted)", marginBottom: "14px" },
  serviceCard: { background: "var(--bg-card)", borderRadius: "12px", padding: "16px", marginBottom: "10px", border: "1px solid var(--border)", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all 0.2s" },
  serviceCardSelected: { border: "1px solid var(--accent)", background: "rgba(233,69,96,0.08)" },
  serviceInfo: { flex: 1 },
  serviceName: { fontWeight: "bold", fontSize: "15px", color: "white", margin: "0 0 4px" },
  serviceDesc: { color: "var(--text-muted)", fontSize: "12px", margin: "0 0 4px" },
  serviceDuration: { color: "var(--text-muted)", fontSize: "11px", margin: 0 },
  servicePrice: { textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" },
  priceText: { fontWeight: "bold", fontSize: "1.1rem", color: "var(--accent)", margin: 0 },
  checkbox: { width: "22px", height: "22px", borderRadius: "6px", border: "2px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-secondary)" },
  checkboxSelected: { background: "var(--accent)", border: "2px solid var(--accent)" },
  selectionSummary: { background: "rgba(233,69,96,0.12)", border: "1px solid rgba(233,69,96,0.3)", borderRadius: "12px", padding: "14px 16px", marginBottom: "14px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  summaryText: { color: "var(--accent)", fontWeight: "bold", fontSize: "14px", margin: "0 0 4px" },
  summaryNames: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  summaryTotal: { color: "var(--accent)", fontWeight: "900", fontSize: "1.3rem", margin: "0 0 2px" },
  summaryDuration: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  nextBtn: { width: "100%", padding: "14px", background: "var(--accent)", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "15px", fontWeight: "bold", marginTop: "8px" },
  selectedBanner: { background: "rgba(233,69,96,0.15)", color: "var(--accent)", padding: "12px 16px", borderRadius: "10px", marginBottom: "20px", fontSize: "13px", border: "1px solid rgba(233,69,96,0.3)", lineHeight: 1.6 },
  dateInput: { width: "100%", padding: "12px", borderRadius: "10px", fontSize: "15px", marginBottom: "20px", boxSizing: "border-box", colorScheme: "dark" },
  noSlots: { textAlign: "center", padding: "30px", background: "var(--bg-card)", borderRadius: "10px", border: "1px solid var(--border)" },
  slotsGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "20px" },
  slotBtn: { padding: "12px 6px", border: "1px solid var(--border)", borderRadius: "8px", background: "var(--bg-card)", cursor: "pointer", fontSize: "13px", textAlign: "center", color: "var(--text-primary)" },
  slotSelected: { border: "1px solid var(--accent)", background: "rgba(233,69,96,0.15)", color: "var(--accent)", fontWeight: "bold" },
  slotBooked: { background: "var(--bg-secondary)", color: "var(--text-muted)", cursor: "not-allowed", textDecoration: "line-through" },
  summaryCard: { background: "var(--bg-card)", borderRadius: "12px", padding: "16px", marginBottom: "16px", border: "1px solid var(--border)" },
  availableCouponsBox: { background: "rgba(233,69,96,0.05)", border: "1px solid rgba(233,69,96,0.2)", borderRadius: "12px", padding: "14px", marginBottom: "16px" },
  availableCouponsTitle: { color: "var(--accent)", fontWeight: "bold", fontSize: "13px", margin: "0 0 10px" },
  availableCouponChip: { display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--bg-card)", borderRadius: "8px", padding: "10px 12px", marginBottom: "8px", border: "1px dashed rgba(233,69,96,0.3)", cursor: "pointer" },
  chipCode: { color: "var(--accent)", fontWeight: "bold", fontSize: "13px", letterSpacing: "1px" },
  chipDiscount: { color: "var(--text-secondary)", fontSize: "12px" },
  chipApply: { color: "var(--accent)", fontSize: "12px", fontWeight: "600" },
  couponBox: { background: "var(--bg-card)", borderRadius: "12px", padding: "16px", marginBottom: "16px", border: "1px dashed var(--border)" },
  couponTitle: { fontWeight: "bold", fontSize: "13px", color: "var(--text-secondary)", margin: "0 0 8px" },
  couponRow: { display: "flex", gap: "8px" },
  couponInput: { flex: 1, minWidth: 0, padding: "11px 13px", borderRadius: "8px", fontSize: "14px", letterSpacing: "1px", fontWeight: "bold" },
  applyBtn: { padding: "11px 18px", background: "var(--accent)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "13px", whiteSpace: "nowrap", flexShrink: 0 },
  couponSuccess: { background: "rgba(76,175,80,0.08)", borderRadius: "8px", padding: "14px", border: "1px solid rgba(76,175,80,0.2)" },
  priceRow: { display: "flex", justifyContent: "space-between", marginBottom: "8px" },
  notesLabel: { display: "block", fontWeight: "500", color: "var(--text-secondary)", fontSize: "13px", marginBottom: "8px" },
  textarea: { width: "100%", padding: "12px", borderRadius: "8px", fontSize: "14px", resize: "vertical", fontFamily: "inherit", marginBottom: "16px", boxSizing: "border-box" },
  payAtShop: { background: "rgba(79,195,247,0.1)", color: "#4fc3f7", padding: "12px 16px", borderRadius: "8px", fontSize: "13px", marginBottom: "16px", border: "1px solid rgba(79,195,247,0.2)" },
  confirmBtn: { width: "100%", padding: "14px", background: "var(--accent)", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "15px", fontWeight: "bold" },
  successCard: { background: "var(--bg-card)", borderRadius: "20px", padding: "36px 28px", maxWidth: "440px", width: "100%", textAlign: "center", border: "1px solid var(--border)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" },
  successTitle: { fontSize: "1.6rem", color: "white", margin: "0 0 6px" },
  successSubtitle: { color: "var(--text-muted)", marginBottom: "20px", fontSize: "14px" },
  successDetails: { background: "var(--bg-secondary)", borderRadius: "10px", padding: "14px", marginBottom: "14px", textAlign: "left", border: "1px solid var(--border)" },
  payNote: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "10px", borderRadius: "8px", fontSize: "13px", marginBottom: "16px", border: "1px solid rgba(76,175,80,0.2)" },
  whatsappSection: { background: "rgba(37,211,102,0.08)", border: "1px solid rgba(37,211,102,0.25)", borderRadius: "12px", padding: "16px", marginBottom: "16px" },
  whatsappHint: { color: "#25d366", fontSize: "13px", fontWeight: "600", margin: "0 0 10px" },
  whatsappBtn: { width: "100%", padding: "13px", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "15px", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  whatsappNote: { color: "rgba(37,211,102,0.7)", fontSize: "11px", margin: "8px 0 0" },
  successBtns: { display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" },
  myBookingsBtn: { background: "var(--accent)", color: "white", padding: "12px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "bold" },
  homeBtn: { background: "var(--bg-secondary)", color: "var(--text-secondary)", padding: "12px 20px", border: "1px solid var(--border)", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
};